import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import { TabContext, TabList } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { isNaN } from 'lodash';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ComingSoonPopup from '@/components/ui/ComingSoonPopup';
import CustomTextMask from '@/components/ui/CustomTextMask';
import InformationReturnsPricingDetails from '@/components/ui/InformationReturnsPricingDetails';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
import type {
  CreditsShoppingCartPricing,
  PricingCategory,
} from '@/types/PricingCategory';
import {
  ConvertAmountFormat,
  informationPricingCategoryDetails,
} from '@/utils/utils';

const Pricing = () => {
  const [open, setOpen] = useState(false);
  // const [infoTotalAccordinOpen, setInfoTotalAccordinOpen] = useState(false);
  const [tabvalue, setTabvalue] = React.useState<string>('');

  React.useEffect(() => {
    const sections = document.querySelectorAll<HTMLElement>('section[id]');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTabvalue(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-45% 0px -55% 0px', // adjust sensitivity
        threshold: 0,
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);
  const [totalCredits, setTotalCredits] = useState<number>(0);
  const [creditsPricingCategory, setCreditsPricingCategory] = useState<
    CreditsShoppingCartPricing[]
  >([]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabvalue(newValue);
  };
  const [infoPriceDetailsOpen, setInfoPriceDetailsOpen] = useState(false);
  const [pricingCategories] = useState<PricingCategory[]>(
    informationPricingCategoryDetails
  );
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const { control, getValues, setValue } = useForm<{ InfoSkuCount: number }>({
    mode: 'onChange',
    defaultValues: { InfoSkuCount: 1 },
  });

  const onChangeCredits = () => {
    const credits = Number(getValues('InfoSkuCount') ?? 0);
    setTotalCredits(credits);
    if (credits > 0) {
      let totalRangeCount = 0;
      let total = 0;
      const pricingCategory: CreditsShoppingCartPricing[] = [];
      // eslint-disable-next-line no-plusplus
      for (let x: number = 0; x <= pricingCategories.length; x++) {
        const pricing = {} as CreditsShoppingCartPricing;
        // @ts-ignore
        pricing.PricingCategoryType = pricingCategories[x].PricingCategoryType;
        // @ts-ignore
        pricing.PricingcategoryId = pricingCategories[x].PricingCategoryId;
        // @ts-ignore
        pricing.PerFormRate = pricingCategories[x].Amount;
        // @ts-ignore
        pricing.FromCount = pricingCategories[x]?.FromCount;

        if (
          // @ts-ignore
          credits <= pricingCategories[x].ToCount ||
          // @ts-ignore
          pricingCategories[x].ToCount === -1
        ) {
          pricing.NoofForm = credits - totalRangeCount;
          // @ts-ignore
          pricing.Amount = pricing.NoofForm * pricingCategories[x].Amount;
          total += pricing.Amount;
          pricing.ToCount = credits;
          pricingCategory.push(pricing);
          break;
        } else {
          // @ts-ignore
          pricing.NoofForm = pricingCategories[x].ToCount - totalRangeCount;
          // @ts-ignore
          pricing.Amount = pricing.NoofForm * pricingCategories[x].Amount;
          // @ts-ignore
          totalRangeCount = pricingCategories[x].ToCount;
          total += pricing.Amount;
          pricing.ToCount = totalRangeCount;
          pricingCategory.push(pricing);
        }
      }
      setTotalAmount(total);
      setCreditsPricingCategory(pricingCategory);
    } else {
      setCreditsPricingCategory([]);
      setTotalAmount(0);
    }
  };

  const changeCreditValue = (isIncrement: boolean) => {
    let count = Number(getValues('InfoSkuCount') ?? 0);
    count = isNaN(count) ? 0 : count;
    if (isIncrement) {
      count += 1;
      setValue('InfoSkuCount', count);
    } else if (count !== 0) {
      count -= 1;
      setValue('InfoSkuCount', count);
    }
    onChangeCredits();
  };

  const handleBlurFormFields = () => {
    const count = Number(getValues(`InfoSkuCount`) ?? 0);
    if (count > 0) {
      setValue('InfoSkuCount', count);
      setTotalCredits(count);
    } else {
      setValue('InfoSkuCount', 1);
      setTotalCredits(1);
    }
  };

  return (
    <AppLayout
      meta={
        <Meta
          title="E-file IRS Tax Forms at an Affordable Price with TaxZerone®"
          description="File IRS excise, employment, extension, nonprofit, and information returns affordably. Try FREE, and pay upon filing. Simplify your tax filing with TaxZerone!"
          keywords="efile IRS Extensions, form 2290, efile irs forms, irs forms, irs extension form, authorized IRS e-file provider, excise tax, HVUT, Free File 990-n, Affrodable Tax Software, IRS Employment Taxes Online, Tax-Exempt Forms"
          canonical="pricing/"
          jsonLdPricing="As low as 0.59"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="7524"
        />
      }
    >
      <ComingSoonPopup open={open} setOpen={setOpen} />
      {/* <Popup2290 /> */}
      <Box component="section">
        <Grid
          container
          sx={{
            backgroundColor: '#213e99',
            pt: { xs: 2, sm: 3, md: 5 },
            mt: -0.6,
            mx: '0 !important',
            width: '100% !important',
            backgroundSize: { xs: '25% auto', sm: '15% auto', md: '10% auto' },
          }}
          spacing={2}
          m={0}
          className="wave-bg"
        >
          <Container maxWidth="xl">
            <Grid
              container
              spacing={2}
              m={2}
              pb={{ xs: 1, sm: 3, md: 5 }}
              className="MuiGrid-root-width banner-shape-inner"
              sx={{ mt: { xs: 0, sm: 0, md: 0 } }}
            >
              <Grid
                size={12}
                sx={{
                  textAlign: {
                    lg: 'center',
                    md: 'center',
                    sm: 'center',
                    xs: 'center',
                  },
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box component="header" sx={{}}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
                      fontWeight: 700,
                      mb: 3,
                      color: '#fff',
                    }}
                  >
                    E-file IRS tax forms at an{' '}
                    <b className="green">affordable price</b>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 17, xs: 14 },
                      fontWeight: 400,
                      color: '#f3f3f3',
                    }}
                  >
                    Check out the pricing and file forms online with the IRS
                    accurately and securely.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {/* </Container>
         </Grid>
      <Grid
        container
        sx={{
          backgroundColor: '#213e99',
          pt: 5,
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      > 
        <Container maxWidth="lg" sx={{ mt: -6 }}> */}
            {/* <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12} >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Top-notch features wrapped up
                <Hidden smDown>
                  <br />
                </Hidden>{' '}
                with affordable pricing
              </Typography>
            </Grid>
          </Grid> */}

            <Grid
              container
              spacing={2}
              className="MuiGrid-root-width"
              sx={{ mt: -6 }}
              direction="row"
              justifyContent="center"
            >
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48 }}>
                      <source srcSet="/assets/images/business-icon-app.webp" />
                      <source srcSet="/assets/images/business-icon-app.png" />
                      <img
                        src="/assets/images/business-icon-app.png"
                        alt="Business Tax Forms"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">Business Tax</span>
                    </span>
                    &nbsp;<small>Forms</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13, minHeight: 40 }}>
                    Form 1120-S, 1065, CA FTB - 100S
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 0,
                      color: '#6439b5',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      Starts at
                    </Box>
                    <br />
                    $179.99
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#businesstax"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        backgroundColor: '#6439b5',
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, lg: 3 }}
                textAlign="center"
                sx={{ pt: { xs: '0 !important', lg: '16px !important' } }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48, marginRight: 5 }}>
                      <source srcSet="/assets/images/employment-icon.webp" />
                      <source srcSet="/assets/images/employment-icon.png" />
                      <img
                        src="/assets/images/employment-icon.png"
                        alt="Employment Tax Forms"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">Employment Tax</span>
                    </span>
                    &nbsp;<small>Forms</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13, minHeight: 40 }}>
                    941, 941-X, 941 Schedule R, 940, 943, 943-X, 944, 945,
                    945-X, 94X PIN Request
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 0,
                      color: '#0a7581',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      Starts at
                    </Box>
                    <br />
                    $6.99
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#employment"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        backgroundColor: '#0a7581',
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid
                size={{ xs: 12, sm: 6, lg: 3 }}
                textAlign="center"
                sx={{ pt: { xs: '0 !important', lg: '16px !important' } }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48, marginRight: 5 }}>
                      <source srcSet="/assets/images/nonprofit-icon.webp" />
                      <source srcSet="/assets/images/nonprofit-icon.png" />
                      <img
                        src="/assets/images/nonprofit-icon.png"
                        alt="Exempt Organizations Forms"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">Exempt Org.</span>
                    </span>
                    &nbsp;<small>Forms</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13, minHeight: 40 }}>
                    990-N, 990-EZ, 990, 990-PF, 990-T, 5227,
                    <br /> CA 199, CA 109, 1120-POL
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 0,
                      color: '#c02652',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      Starts at
                    </Box>
                    <br />
                    $9.90
                    <sup />
                    {/* <Box
                      sx={{
                        fontSize: 12,
                        fontWeight: 300,
                        color: '#444',
                        mb: 0,
                        lineHeight: 1.3,
                      }}
                    >
                      *Form 990-N filed on time for the current tax year.
                    </Box> */}
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#exempt"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        backgroundColor: '#c02652',
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, lg: 3 }}
                textAlign="center"
                sx={{ pt: { xs: '0 !important', lg: '16px !important' } }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48 }}>
                      <source srcSet="/assets/images/information-icon.webp" />
                      <source srcSet="/assets/images/information-icon.png" />
                      <img
                        src="/assets/images/information-icon.png"
                        alt="Information Returns"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">Information</span>
                    </span>
                    &nbsp;<small>Returns</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13, minHeight: 40 }}>
                    W-2, 1099, W-2G, 1097, 5498, 3921, 3922, 1098, and 1095
                    Forms
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 0,
                      color: '#4269e2',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      As low as
                    </Box>
                    <br />
                    $0.59
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#informationreturns"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        backgroundColor: '#4269e2',
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid
                size={{ xs: 12, sm: 6, lg: 3 }}
                textAlign="center"
                sx={{ pt: { xs: '0 !important', lg: '0px !important' } }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 3,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: { xs: 2, lg: 2 },
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48 }}>
                      <source srcSet="/assets/images/excise-icon.webp" />
                      <source srcSet="/assets/images/excise-icon.png" />
                      <img
                        src="/assets/images/excise-icon.png"
                        alt="Excise Tax Forms"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">Excise Tax</span>
                    </span>
                    &nbsp;<small>Forms</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    2290, 8849, 2290 Amendments
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 0,
                      color: '#1da94a',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      Starts at
                    </Box>
                    <br />
                    $19.99
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#excise"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        backgroundColor: '#1da94a',
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, lg: 3 }}
                textAlign="center"
                sx={{ pt: { xs: '0 !important', sm: '0px !important' } }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48 }}>
                      <source srcSet="/assets/images/extension-icon.webp" />
                      <source srcSet="/assets/images/extension-icon.png" />
                      <img
                        src="/assets/images/extension-icon.png"
                        alt="Extension Forms"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">Extension</span>
                    </span>
                    &nbsp;<small>Forms</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13, minHeight: 40 }}>
                    7004, 4868, 8868, 8809, 15397
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 0,
                      color: '#213e99',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      Starts at
                    </Box>
                    <br />
                    $4.99
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#extension"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        backgroundColor: '#213e99',
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid
                size={{ xs: 12, sm: 6, lg: 3 }}
                textAlign="center"
                sx={{ pt: '0 !important' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: 2,
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48, marginRight: 5 }}>
                      <source srcSet="/assets/images/other-icon.webp" />
                      <source srcSet="/assets/images/other-icon.png" />
                      <img
                        src="/assets/images/other-icon.png"
                        alt="Essential Forms"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">Essential </span>
                    </span>
                    &nbsp;<small>Forms</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Form W-9, Form W-8BEN, Form 8655, FinCEN BOI Report
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#4b42ad',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    FREE*
                    <Box
                      sx={{
                        fontSize: 12,
                        fontWeight: 300,
                        color: '#444',
                        mb: 0,
                        lineHeight: 1.3,
                      }}
                    >
                      *Request Form W-9 or W-8BEN online to collect Taxpayer
                      Identification and Certification.
                    </Box>
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#essential"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        background: '#4b42ad',
                        '&:hover, &:focus, &:active': {
                          background: '#172b6b',
                        },
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* <Grid size={{xs: 12, sm: 6, lg: 3}} xl={3} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    pt: 3,
                    pb: 2,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 5,
                    minHeight: '100%',
                    mt: { xs: 2, lg: 3 },
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 17, sm: 20, md: 21, lg: 19 },
                      fontWeight: 600,
                      mb: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <picture style={{ width: 48, height: 48, marginRight: 5 }}>
                      <source srcSet="/assets/images/boi-icon.webp" />
                      <source srcSet="/assets/images/boi-icon.png" />
                      <img
                        src="/assets/images/boi-icon.png"
                        alt="FinCEN BOI Report"
                        loading="lazy"
                        width={48}
                        height={48}
                      />
                    </picture>
                    <span className="highlight-container">
                      <span className="highlight">FinCEN BOI</span>
                    </span>
                    &nbsp;<small>Report</small>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>BOI Reporting</Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 0,
                      color: '#2c5257',
                      mb: 0,
                      pb: { xs: 6, lg: 5 },
                    }}
                  >
                    <Box
                      component={'span'}
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      Starts at
                    </Box>
                    <br />
                    $39.99
                  </Typography>
                  <Box
                    sx={{ position: 'absolute', bottom: 24, right: 0, left: 0 }}
                  >
                    <Button
                      href="#boir"
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 0,
                        mb: 0,
                        backgroundColor: '#2c5257',
                      }}
                    >
                      See detailed pricing
                    </Button>
                  </Box>
                </Paper>
              </Grid> */}
            </Grid>
          </Container>
        </Grid>
        <Box
          sx={{
            backgroundColor: '#f0f2f9',
            pt: 2,
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
          }}
        >
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={0}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center" mt={3} mb={0}>
              <Container
                maxWidth="md"
                sx={{ mb: 5, backgroundColor: '#fff', borderRadius: 4, p: 2 }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="center"
                  spacing={{ xs: 0, sm: 3 }}
                >
                  <List sx={{ p: 0, m: 0, width: '100%' }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckCircleRoundedIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 500, fontSize: 14 }}
                          color="secondary"
                        >
                          Form-based and interview-based filing
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckCircleRoundedIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 500,
                            fontSize: 14,
                            whiteSpace: { xs: 'normal', md: 'normal' },
                          }}
                          color="secondary"
                        >
                          Step-by-step instructions to complete filing quickly
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckCircleRoundedIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 500,
                            fontSize: 14,
                            whiteSpace: { xs: 'normal', md: 'normal' },
                          }}
                          color="secondary"
                        >
                          Smart validation checks to reduce errors and
                          rejections
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  </List>{' '}
                  <List sx={{ p: 0, m: 0, width: { xs: '100%', sm: '65%' } }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckCircleRoundedIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 500, fontSize: 14 }}
                          color="secondary"
                        >
                          Secure e-filing with the IRS
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckCircleRoundedIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 500, fontSize: 14 }}
                          color="secondary"
                        >
                          Instant return updates
                        </Typography>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <CheckCircleRoundedIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 500, fontSize: 14 }}
                          color="secondary"
                        >
                          Multiple support channels
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Stack>
              </Container>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                E-file IRS forms at the lowest price and stay tax compliant
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mt: 4,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
              >
                E-file Now
              </Button>
            </Grid>
          </Grid>
          <Container maxWidth="md" sx={{ pb: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 0,
                  py: 0.5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 3,
                  minHeight: '100%',
                  backgroundColor: 'transparent',
                  width: '100%',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <picture>
                    <source srcSet="/assets/images/irs-icon.webp" />
                    <source srcSet="/assets/images/irs-icon.png" />
                    <img
                      src="/assets/images/irs-icon.png"
                      alt="IRS-authorized"
                      loading="lazy"
                      width={48}
                      height={48}
                    />
                  </picture>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    IRS-authorized
                  </Box>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 0,
                  py: 0.5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 3,
                  minHeight: '100%',
                  backgroundColor: 'transparent',
                  width: '100%',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <picture>
                    <source srcSet="/assets/images/easy-icon.webp" />
                    <source srcSet="/assets/images/easy-icon.png" />
                    <img
                      src="/assets/images/easy-icon.png"
                      alt="Simple Process"
                      loading="lazy"
                      width={48}
                      height={48}
                    />
                  </picture>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Simple Process
                  </Box>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 0,
                  py: 0.5,
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 3,
                  minHeight: '100%',
                  backgroundColor: 'transparent',
                  width: '100%',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <picture>
                    <source srcSet="/assets/images/secure-icon.webp" />
                    <source srcSet="/assets/images/secure-icon.png" />
                    <img
                      src="/assets/images/secure-icon.png"
                      alt="Completely secure"
                      loading="lazy"
                      width={48}
                      height={48}
                    />
                  </picture>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Completely secure
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Container>
        </Box>
        <Box
          sx={{
            background: 'rgba(255,255, 255, .85)',
            backdropFilter: 'saturate(50%) blur(5px)',
            position: 'sticky',
            top: 64,
            zIndex: 9,
          }}
        >
          <Container maxWidth="xl" sx={{ textAlign: 'center', pt: 1 }}>
            <TabContext value={tabvalue}>
              <TabList
                variant="scrollable"
                onChange={handleTabChange}
                scrollButtons
                allowScrollButtonsMobile
                aria-label="price tabs"
                sx={{
                  maxWidth: 1275,
                  mx: 'auto',
                  alignItems: 'center',
                  '& .MuiTabs-flexContainer .MuiButtonBase-root': {
                    textTransform: 'none',
                    color: '#637381',
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    py: 0.75,
                    px: 1.5,
                    mx: 0.5,
                    minHeight: 'inherit',
                    transition: 'all 0.3s ease 0s',
                    mt: 1,
                  },
                  '& .MuiTabs-flexContainer .MuiButtonBase-root:hover': {
                    borderColor: '#213e99',
                    backgroundColor: 'rgba(33, 62, 153, 0.10)',
                  },
                  '& .MuiTabs-scroller': { minHeight: 48 },
                  '& .MuiTabs-indicator': { height: 5 },
                  '& .MuiTab-root.Mui-selected': {
                    color: '#fff !important',
                    borderColor: '#213e99 !important',
                    backgroundColor: '#213e99 !important',
                  },
                }}
              >
                <Tab
                  value="businesstax"
                  label="Business Tax Forms"
                  href="#businesstax"
                />
                <Tab
                  value="employment"
                  label={<React.Fragment>Employment Tax Forms</React.Fragment>}
                  href="#employment"
                />
                <Tab value="exempt" label="Exempt Org. Forms" href="#exempt" />
                <Tab
                  value="informationreturns"
                  label={<React.Fragment>Information Returns</React.Fragment>}
                  href="#informationreturns"
                />
                <Tab value="excise" label="Excise Tax Forms" href="#excise" />{' '}
                <Tab
                  value="extension"
                  label="Extension Forms"
                  href="#extension"
                />
                <Tab
                  value="essential"
                  label="Essential Forms"
                  href="#essential"
                />
              </TabList>
            </TabContext>
          </Container>
        </Box>

        {/* Businesstax Price start */}
        <Grid
          component="section"
          container
          sx={{
            backgroundColor: '#fff',
            scrollMarginTop: 120,
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
            mb: 0,
          }}
          spacing={2}
          m={0}
          id="businesstax"
        >
          <Container maxWidth="lg">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={5}
              pb={0}
              className="MuiGrid-root-width"
            >
              <Grid size={12}>
                {/* <Box
                  component={'div'}
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#6439b5' },
                  }}
                > */}
                <Link
                  href="/business-tax/"
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#6439b5' },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 2,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    File Your <b>Business Tax Forms</b> at the lowest prices
                    <br className="hide-xs" />
                    in the industry.
                  </Typography>
                </Link>
                {/* </Box> */}
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 3,
                    color: '#444',
                    textAlign: 'center',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{ color: '#6439b5' }}>Federal</span> Business Tax
                  Returns
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5.5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                    // mx: { lg: 40, md: 40, sm: 0, xs: 0 },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: { xs: 0, sm: 2, md: 1 },
                    }}
                  >
                    Form 1120-S
                  </Typography>
                  <Typography variant="body1">
                    U.S. Income Tax Return for an S Corporation
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 4,
                      color: '#000000ff',
                      mb: 0,
                    }}
                  >
                    $179.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      / return
                    </Box>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    (Tax Year - 2025 &amp; 2024)
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5.5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                    mx: { lg: 0, md: 0, sm: 0, xs: 0 },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: { xs: 0, sm: 2, md: 4 },
                    }}
                  >
                    Form 1065
                  </Typography>
                  <Typography variant="body1">
                    U.S. Return of Partnership Income
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 4,
                      color: '#000000ff',
                      mb: 0,
                    }}
                  >
                    $179.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      / return
                    </Box>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    (Current Tax Year - 2025)
                  </Typography>
                </Paper>
              </Grid>

              <Grid
                size={{ xs: 12 }}
                sx={{ mt: 3, borderTop: '1px solid #ccc' }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 3,
                    color: '#444',
                    textAlign: 'center',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{ color: '#6439b5' }}>California State</span>{' '}
                  Business Tax Returns
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5.5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    mx: { lg: 0, md: 0, sm: 0, xs: 0 },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: { xs: 0, sm: 2, md: 1 },
                    }}
                  >
                    CA FTB - Form 100S
                  </Typography>
                  <Typography variant="body1">
                    California S Corporation Franchise or Income Tax Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 4,
                      color: '#6439b5',
                      mb: 0,
                    }}
                  >
                    $69.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      / return
                    </Box>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    (Current Tax Year - 2025)
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5.5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                    mx: { lg: 0, md: 0, sm: 0, xs: 0 },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: { xs: 0, sm: 2, md: 1 },
                    }}
                  >
                    CA FTB – Form 565
                  </Typography>
                  <Typography variant="body1">
                    California Partnership Return of Income
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 4,
                      color: '#000000ff',
                      mb: 0,
                    }}
                  >
                    $69.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      / return
                    </Box>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    (Current Tax Year - 2025)
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5.5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    mx: { lg: 0, md: 0, sm: 0, xs: 0 },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: { xs: 0, sm: 2, md: 1 },
                    }}
                  >
                    CA FTB – Form 568
                  </Typography>
                  <Typography variant="body1">
                    California Limited Liability Company Return of Income
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 4,
                      color: '#6439b5',

                      mb: 0,
                    }}
                  >
                    $69.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      / return
                    </Box>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    (Current Tax Year - 2025)
                  </Typography>
                </Paper>
              </Grid>

              {/* <Hidden lgDown>
                <Grid size={12}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mt: 3,

                      textAlign: 'center',
                      textDecoration: 'none',
                      '& b': { color: '#6439b5' },
                    }}
                  >
                    <b> FREE</b> Extension for Your Business Tax
                  </Typography>
                </Grid>
                <Grid size={12} textAlign="center">
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      py: { xs: 2, sm: 3, md: 5 },
                      textAlign: 'center',
                      borderWidth: 2,
                      borderColor: '#6439b5',
                      borderRadius: 2,
                      minHeight: '100%',
                      mx: { md: 30, sm: 0, xs: 0 },
                      position: 'relative',
                      background: 'linear-gradient(135deg, #ddf1e8, #ffd6ff)',
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 22 },
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      For Form 7004
                    </Typography>
                    <Stack
                      spacing={6}
                      direction="row"
                      sx={{ justifyContent: 'center' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: 28, sm: 34, md: 40 },
                          fontWeight: 700,
                          mt: 4,
                          color: '#000000ff',
                          mb: 0,
                        }}
                      >
                        $17.99
                        <Box
                          component={'span'}
                          sx={{
                            fontSize: { xs: 18, sm: 24, md: 24 },
                            fontWeight: 300,
                            color: '#444',
                          }}
                        >
                          / Normal Fee
                        </Box>
                      </Typography>

                      <Box
                        component={'span'}
                        sx={{
                          fontSize: { xs: 28, sm: 34, md: 40 },
                          fontWeight: 700,
                        }}
                      >
                        $0
                        <Box
                          component={'span'}
                          sx={{
                            fontSize: { xs: 18, sm: 24, md: 24 },
                            fontWeight: 300,
                            color: '#444',
                          }}
                        >
                          / You Pay
                        </Box>
                      </Box>
                    </Stack>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mt: 3,
                          fontWeight: 600,
                          fontSize: { xs: 18, sm: 24, md: 26 },
                        }}
                      >
                        Condition:
                      </Typography>
                      <Typography variant="body1">
                        Pay the full{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1065 filing fee of $179.99 upfront
                        </span>{' '}
                        and receive:
                      </Typography>
                      <Box
                        component="ul"
                        sx={{
                          mb: 0,
                          ml: 0,
                          '& li': {
                            mb: 1.5,
                            listStyle: 'none',
                            position: 'relative',
                            pl: 0, // space for the checkmark
                            '&::before': {
                              content: '"✔"', // green checkmark
                              position: 'absolute',
                              left: -20,
                              top: 0,
                              color: '#6439b5',
                              fontWeight: 'bold',
                            },
                          },
                        }}
                      >
                        <li>
                          Extended deadline until{' '}
                          <span style={{ fontWeight: 600 }}>September 15</span>
                        </li>
                        <li> No penalties or late filing stress</li>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Hidden> */}
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={0}
              pb={2}
              mb={6}
              className="MuiGrid-root-width"
            >
              <Grid size={12} textAlign="center">
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    mt: 1,
                  }}
                >
                  Get the required supporting forms and schedules FREE with your
                  return.
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 2, sm: 2 },
                    backgroundColor: '#6439b5 !important',
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
                >
                  Get Started Now!
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Grid>
        {/* Businesstax Price end */}
        {/* Employment Tax Price start */}
        <Grid
          component="section"
          container
          sx={{
            backgroundColor: '#f0f2f9',
            scrollMarginTop: 120,
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
            mb: 0,
          }}
          spacing={2}
          m={0}
          id="employment"
        >
          <Container maxWidth="lg">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={5}
              pb={0}
              className="MuiGrid-root-width"
            >
              <Grid size={12}>
                <Link
                  href="/employment-tax/"
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#0a7581' },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 3,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    E-file IRS <b>Employment Tax Forms</b> at the
                    <br className="hide-xs" /> best price in the industry
                  </Typography>
                </Link>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 28, md: 24, sm: 18, xs: 16 },
                    fontWeight: 500,
                    mb: 3,
                    textAlign: 'center',
                    '& b': { color: '#0a7581' },
                  }}
                >
                  <b> Quarterly</b> IRS Employment Tax Forms
                </Typography>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              className="MuiGrid-root-width"
              justifyContent="center"
            >
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fff',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 941
                  </Typography>
                  <Typography variant="body1">
                    Employer&apos;s Quarterly Federal
                    <br />
                    Tax Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 3,
                      color: '#0a7581',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#f0f2f9',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 941-X
                  </Typography>
                  <Typography variant="body1">
                    Amend Your Quarterly Employment <br />
                    Tax Returns
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fff',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 941 Schedule R
                  </Typography>
                  <Typography variant="body1">
                    Aggregate Form 941 Reporting for CPEOs, PEOs,{' '}
                    {/* <Hidden mdDown>
                      <br />
                    </Hidden> */}
                    and IRS Agents
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#0a7581',
                      mb: 0,
                    }}
                  >
                    $9.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.8, fontStyle: 'italic' }}
                  >
                    Plus an additional{' '}
                    <span style={{ fontWeight: 600 }}>$1</span> fee per client.
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={12} sx={{ borderTop: '1px solid #ccc', mt: 6 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 28, md: 24, sm: 18, xs: 16 },
                    fontWeight: 500,
                    mt: 3,
                    mb: 3,
                    textAlign: 'center',
                  }}
                >
                  <b style={{ color: '#0a7581' }}> Annual</b> IRS Employment Tax
                  Forms
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fff',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 940
                  </Typography>
                  <Typography variant="body1">
                    Employer&apos;s Annual Federal Unemployment (FUTA) Tax
                    Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#0a7581',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#f0f2f9',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 940 Amendment
                  </Typography>
                  <Typography variant="body1">
                    Amend Your Employer's Annual Federal Unemployment (FUTA) Tax
                    Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#0a7581',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,

                    backgroundColor: '#fff',
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 943
                  </Typography>
                  <Typography variant="body1">
                    Employer&apos;s Annual Federal Tax Return
                    <br />
                    for Agricultural Employees
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    backgroundColor: '#f0f2f9',

                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 943-X
                  </Typography>
                  <Typography variant="body1">
                    Adjusted Employer&apos;s Annual Federal Tax Return for
                    Agricultural Employees
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#0a7581',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fff',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 944
                  </Typography>
                  <Typography variant="body1">
                    Employer&apos;s Annual Federal Tax Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#f0f2f9',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 945
                  </Typography>
                  <Typography variant="body1">
                    Annual Return of Withheld Federal Income Tax
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#0a7581',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fff',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 945-X
                  </Typography>
                  <Typography variant="body1">
                    Adjusted Annual Return of Withheld Federal Income Tax for
                    Nonpayroll Payments
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $6.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    backgroundColor: '#fff',
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 94x On-Line Signature PIN
                  </Typography>
                  <Typography variant="body1">
                    Apply for a 10-digit signature PIN and become an IRS
                    Authorized Signer to e-file Employment Tax Forms
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#0a7581',
                      mb: 0,
                    }}
                  >
                    FREE
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={0}
            pb={2}
            mb={6}
            className="MuiGrid-root-width"
            sx={{ width: '100%' }}
          >
            <Grid size={12} textAlign="center" mt={3} mb={0}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                File an employment tax form with the IRS and stay tax compliant.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mt: { xs: 2, sm: 4 },
                  backgroundColor: '#0a7581 !important',
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
              >
                E-File Form 941
              </Button>
            </Grid>
          </Grid>
        </Grid>
        {/* Employment Tax Price end */}

        {/* Exempt Org. Price start */}
        <Grid
          component="section"
          container
          sx={{
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
            mb: 0,
            backgroundColor: '#fff',
            scrollMarginTop: 120,
          }}
          spacing={2}
          m={0}
          id="exempt"
        >
          <Container maxWidth="lg">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={5}
              pb={0}
              className="MuiGrid-root-width"
            >
              <Grid size={12}>
                <Link
                  href="/tax-exempt/"
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#c02652' },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 3,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    E-file IRS forms for your <b>Exempt Organization</b>
                    <br className="hide-xs" /> at the lowest price in the
                    industry
                  </Typography>
                </Link>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              className="MuiGrid-root-width"
              justifyContent="center"
            >
              {/* <Grid size={{ xs: 12, md: 6, sm: 6 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    position: 'relative',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 990-N (e-Postcard)
                  </Typography>
                  <Typography variant="body1">
                    Annual Electronic Filing Requirement for
                    <Hidden mdDown>
                      <br />
                    </Hidden>{' '}
                    Small Exempt Organizations
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 1.7,
                      color: '#c02652',
                      mb: -0.6,
                    }}
                  >
                    $9.90
                    <sup>
                      <small>*</small>
                    </sup>
                  </Typography>
                  <Box
                    component={'span'}
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Current Tax Year - <b>2025</b>)
                  </Box>
                  <Box
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.2,
                      fontWeight: 300,
                      color: '#444',
                      position: { md: 'absolute' },
                      bottom: 20,
                      left: 0,
                      right: 0,
                    }}
                  >
                    *Form 990-N filed on time for the current tax year.
                  </Box>
                </Paper>
              </Grid> */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 990-N (e-Postcard)
                  </Typography>
                  <Typography variant="body1">
                    Annual Electronic Filing Requirement for
                    <Hidden mdDown>
                      <br />
                    </Hidden>{' '}
                    Small Exempt Organizations
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    $9.90
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Tax Year - <b>2025, 2024, 2023</b>)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 990-EZ
                  </Typography>
                  <Typography variant="body1">
                    Short Form Return of Organization
                    <br /> Exempt From Income Tax
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $89.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Tax Year - <b>2025, 2024, 2023</b>)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 990
                  </Typography>
                  <Typography variant="body1">
                    Return of Organization Exempt
                    <br /> From Income Tax
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    $179.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Tax Year - <b>2025, 2024, 2023</b>)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 990-PF
                  </Typography>
                  <Typography variant="body1">
                    Return of Private Foundation
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $149.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Tax Year - <b>2025, 2024, 2023</b>)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 990 Amendment
                  </Typography>
                  <Typography variant="body1">
                    990, 990-EZ, 990-PF, and 990-T
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    $49.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 12, sm: 13, md: 15 },
                      fontWeight: 300,
                      color: '#444',
                      lineHeight: 1.3,
                      display: 'inline-block',
                      // mt: -3,
                    }}
                  >
                    (For the returns originally filed with TaxZerone)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 990-T
                  </Typography>
                  <Typography variant="body1">
                    Exempt Organization Business Income Tax Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#00',
                      mb: 0,
                    }}
                  >
                    $129.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Tax Year - <b>2025, 2024, 2023</b>)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 5227
                  </Typography>
                  <Typography variant="body1">
                    Split-Interest Trust Information Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    $179.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    ( Tax Year - <b>2025, 2024</b>)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    CA Form 199
                  </Typography>
                  <Typography variant="body1">
                    California Exempt Organization Annual Information Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $69.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Tax Year - <b>2025, 2024, 2023</b>)
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    CA Form 199 Amendment
                  </Typography>
                  <Typography variant="body1">
                    Amend Your California Exempt Organization Annual Information
                    Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    $49.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 14, md: 15 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Available for returns originally filed through TaxZerone)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    CA Form 109
                  </Typography>
                  <Typography variant="body1">
                    California Exempt Organization Business Income Tax Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    $69.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Current Tax Year - <b> 2025</b>)
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 1120-POL
                  </Typography>
                  <Typography variant="body1">
                    U.S. Income Tax Return for Certain Political Organizations
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $49.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: 14, sm: 14, md: 15 },
                      fontWeight: 300,
                      color: '#444',
                      // mt: -3,
                    }}
                  >
                    (Current Tax Year - <b>2025</b>)
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 3,
                    color: '#444',
                    textAlign: 'center',
                    textDecoration: 'none',
                  }}
                >
                  Tax-Exempt <span style={{ color: '#c02652' }}>Twin Set</span>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990-EZ and 990-T
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $219.98
                    </Box>
                    <br />
                    $169.98
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $50
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990 and 990-T
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $309.98
                    </Box>
                    <br />
                    $259.98
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $50
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990-PF and 990-T
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $279.98
                    </Box>
                    <br />
                    $229.98
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $50
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={12} sx={{ borderTop: '1px solid #ccc', mt: 6 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 3,
                    color: '#c02652',
                    textAlign: 'center',
                    textDecoration: 'none',
                  }}
                >
                  Federal <span style={{ color: '#444' }}>and</span> CA Filing{' '}
                  <span style={{ color: '#444' }}>Combos</span>
                </Typography>
              </Grid>
              {/* <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990-N and CA 199
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $79.89
                    </Box>
                    <br />
                    $39.89
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $40
                  </Typography>
                </Paper>
              </Grid> */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990-EZ and CA 199
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $159.98
                    </Box>
                    <br />
                    $119.98
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $40
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990 and CA 199
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $249.98
                    </Box>
                    <br />
                    $209.98
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $40
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#f0f2f9',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990-PF and CA 199
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $219.98
                    </Box>
                    <br />
                    $179.98
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $40
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-file 990-T and CA 199
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#c02652',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $199.98
                    </Box>
                    <br />
                    $159.98
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $40
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={0}
            pb={2}
            mb={6}
            className="MuiGrid-root-width"
            sx={{ width: '100%' }}
          >
            <Grid size={12} textAlign="center" mt={3} mb={0}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                E-file IRS forms accurately and stay tax-exempt
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mt: { xs: 2, sm: 4 },
                  mb: 3,
                  backgroundColor: '#c02652 !important',
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
              >
                Stay Exempt Now
              </Button>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxWidth: 1100,
                  display: 'flex',
                  m: 'auto',
                }}
              >
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    textAlign: 'left',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <Box
                      component="img"
                      src="/assets/images/paypal-logo.svg"
                      alt="Now accepting PayPal!"
                      sx={{
                        maxWidth: { xs: 100, sm: 100, md: 120 },
                        alignSelf: { xs: 'flex-start', md: 'center' },
                      }}
                    />
                    <Box>
                      Now accepting <b>PayPal!</b> You can conveniently pay your
                      filing fee using PayPal and other available payment
                      methods.
                    </Box>
                  </Stack>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        {/* Exempt Org. Price end */}

        {/* Information Returns Price start */}
        <Grid
          component="section"
          container
          sx={{
            backgroundColor: '#f0f2f9',
            scrollMarginTop: 120,
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
            mb: 0,
          }}
          spacing={2}
          m={0}
          id="informationreturns"
        >
          <Container maxWidth="lg">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={5}
              pb={0}
              className="MuiGrid-root-width"
            >
              <Grid size={12}>
                <Link
                  href="/information-returns/"
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#4269e2' },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 2,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    E-file <b>Information Returns</b> at the
                    <br className="hide-xs" /> best price in the industry
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                      fontWeight: 400,
                      mb: 3,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    W-2, W-2G, 1095, 1097, 1098, 1099, 3921, 3922, and 5498 –
                    Federal Filing
                  </Typography>
                </Link>
              </Grid>
            </Grid>
            <Grid container spacing={2} className="MuiGrid-root-width">
              <Grid size={12} textAlign="center">
                <Box
                  sx={{
                    p: 0,
                    py: { xs: 0, sm: 0, md: 0 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& td': {
                          // display: { xs: 'block', sm: 'revert' },
                        },
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },

                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'left' },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'center' },
                        },
                        '& tr:last-child td': { border: 0 },
                      }}
                      className="table-list"
                    >
                      <TableHead
                        sx={{ borderBottom: 2, borderColor: '#a6a6a6' }}
                      >
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              fontWeight: 600,
                              borderRadius: '8px 0px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            No. of Forms
                          </TableCell>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              width: { xs: 150, sm: 180 },
                              textAlign: { xs: 'center', sm: 'center' },
                              fontWeight: 600,
                              borderRadius: '0px 8px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            Price per form
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            1 to 25
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#4269e2',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $2.49
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            26 to 50
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            51 to 100
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.59
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            101 to 250
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.29
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            251 to 500
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $1.09
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            501 to 1000
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $0.79
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            1000+
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: '2px solid #a6a6a6 !important',
                            }}
                          >
                            $0.59
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={0}
              pb={2}
              mb={6}
              className="MuiGrid-root-width"
            >
              <Grid size={12} textAlign="center" sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& td': {
                          display: { xs: 'block', sm: 'revert' },
                        },
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          textAlign: { xs: 'right', sm: 'center' },
                        },
                        '& tr td': { border: 0 },
                      }}
                    >
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              textAlign: { xs: 'left', sm: 'right' },
                              fontWeight: 600,
                            }}
                          >
                            Enter No. of Forms
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#4269e2',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                              width: { sm: 180 },
                              px: 0,
                            }}
                          >
                            <Box>
                              <Controller
                                name="InfoSkuCount"
                                control={control}
                                defaultValue={1}
                                render={({
                                  field: { onChange, value },
                                  fieldState: { error },
                                }) => (
                                  <>
                                    <TextField
                                      id="InfoSkuCount"
                                      label=""
                                      variant="outlined"
                                      color="secondary"
                                      value={value?.toString()}
                                      size="small"
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          '& input': {
                                            textAlign: 'center',
                                          },
                                        },
                                      }}
                                      onChange={(event) => {
                                        onChange(event);
                                        onChangeCredits();
                                      }}
                                      onBlur={() => {
                                        handleBlurFormFields();
                                      }}
                                      error={!!error}
                                      helperText={error ? error.message : null}
                                      InputProps={{
                                        inputComponent: CustomTextMask as any,
                                        inputProps: {
                                          mask: 'num',
                                          blocks: {
                                            num: {
                                              min: 1,
                                              max: 9999999,
                                              mask: Number,
                                              scale: 0,
                                            },
                                            inputMode: 'numeric',
                                            maxLength: 7,
                                          },
                                        },
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <IconButton
                                              aria-label=""
                                              disabled={value <= 1}
                                              onClick={() =>
                                                changeCreditValue(false)
                                              }
                                              edge="end"
                                            >
                                              <RemoveOutlinedIcon />
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              aria-label=""
                                              disabled={value >= 9999999}
                                              onClick={() =>
                                                changeCreditValue(true)
                                              }
                                              edge="end"
                                            >
                                              <AddOutlinedIcon />
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </>
                                )}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <TableContainer
                  sx={{
                    width: '100%',
                    fontWeight: 400,
                    fontSize: 15,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    maxWidth: 836,
                    mx: 'auto',
                    mt: 1,
                  }}
                >
                  <Table
                    sx={{
                      '& td': {},
                      '& tr td:first-of-type': {
                        py: { xs: 0.5, sm: 1 },
                      },
                      '& tr td:last-child': {
                        py: { xs: 0.25, sm: 0.5 },
                        textAlign: { xs: 'right', sm: 'center' },
                      },
                      '& tr td': { border: 0 },
                    }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell
                          scope="row"
                          sx={{
                            fontSize: 18,
                            textAlign: { xs: 'left', sm: 'right' },
                            fontWeight: 600,
                          }}
                        >
                          Total Price:
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: '#4269e2',
                            borderLeft: 2,
                            borderColor: '#a6a6a6',
                            width: { sm: 180 },
                            position: 'relative',
                            px: 3,
                          }}
                        >
                          {ConvertAmountFormat(totalAmount)}
                          {totalCredits > 25 && (
                            <Tooltip
                              title={`${
                                infoPriceDetailsOpen === false
                                  ? 'View the breakdown of forms'
                                  : 'Hide'
                              }`}
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setInfoPriceDetailsOpen(
                                    infoPriceDetailsOpen === false
                                  )
                                }
                                sx={{
                                  background: 'rgba(0, 0, 0, 0.85)',
                                  position: 'absolute',
                                  right: 0,
                                  top: 8,
                                  p: 0,
                                  '&:hover, &:active, &:focus': {
                                    background: 'rgba(0, 0, 0, 1)',
                                  },
                                }}
                              >
                                {infoPriceDetailsOpen === false ? (
                                  <KeyboardArrowDownIcon
                                    sx={{ color: '#fff' }}
                                  />
                                ) : (
                                  <KeyboardArrowUpIcon sx={{ color: '#fff' }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                {infoPriceDetailsOpen === true && (
                  <InformationReturnsPricingDetails
                    creditsPricingCategory={creditsPricingCategory}
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#007867',
                    borderRadius: 2,
                    minHeight: '100%',
                    display: 'gird',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e0f5ee',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    State Filing
                    <br />
                    (Form W-2 & 1099)
                  </Typography>
                  <Typography variant="body1">
                    State filing ensures your W-2 and 1099 forms comply with
                    state requirements.
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#007867',
                      mb: 0,
                    }}
                  >
                    $0.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      {' '}
                      per form
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 4.3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#006c9c',
                    borderRadius: 2,
                    minHeight: '100%',
                    display: 'gird',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e0f9fd',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Postal Mailing
                  </Typography>
                  <Typography variant="body1">
                    TaxZerone provides postal mailing services, delivering
                    recipient copies quickly to meet IRS deadlines.
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#006c9c',
                      mb: 0,
                    }}
                  >
                    $1.75
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      {' '}
                      per form
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#5119b7',
                    borderRadius: 2,
                    minHeight: '100%',
                    display: 'gird',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#efe4fc',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Electronic Delivery
                  </Typography>
                  <Typography variant="body1">
                    Send information returns forms instantly to recipients
                    through ZeroneVault, a secure online portal.
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#5119b7',
                      mb: 0,
                    }}
                  >
                    $0.50
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      {' '}
                      per form
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={12} textAlign="center">
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    mt: 1,
                  }}
                >
                  Complete your information return filing and stay tax
                  compliant.
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 2, sm: 2 },
                    backgroundColor: '#4269e2 !important',
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
                >
                  Start Filing
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Grid>
        {/* Information Retruns Price end */}
        {/* Excise Tax Price start */}
        <Grid
          component="section"
          container
          sx={{
            backgroundColor: '#fff',
            scrollMarginTop: 120,
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
            mb: 0,
          }}
          spacing={2}
          m={0}
          id="excise"
        >
          <Container maxWidth="lg">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={5}
              pb={0}
              className="MuiGrid-root-width"
            >
              <Grid size={12}>
                <Link
                  href="/excise-tax/"
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#1da94a' },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 3,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    E-file IRS <b>Excise Tax Form 2290</b> at the
                    <br className="hide-xs" /> best price in the industry
                  </Typography>
                </Link>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              className="MuiGrid-root-width"
              justifyContent="center"
              mb={8}
            >
              <Grid size={12}>
                <Box
                  sx={{
                    p: 0,
                    py: { xs: 0, sm: 0, md: 0 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <TableContainer>
                    <Table
                      sx={{
                        '& td': {
                          // display: { xs: 'block', sm: 'revert' },
                        },
                        '& tr td:first-of-type': {
                          py: { xs: 0.5, sm: 1 },

                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'left' },
                        },
                        '& tr td:last-child': {
                          py: { xs: 0.25, sm: 0.5 },
                          borderBottom: {
                            xs: '1px solid #a6a6a6',
                            sm: '1px solid #a6a6a6',
                          },
                          textAlign: { xs: 'center', sm: 'center' },
                        },
                        '& tr:last-child td': { border: 0 },
                      }}
                      className="table-list"
                    >
                      <TableHead
                        sx={{ borderBottom: 2, borderColor: '#a6a6a6' }}
                      >
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              fontWeight: 600,
                              borderRadius: '8px 0px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            No. of Vehicles
                          </TableCell>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                              width: { xs: 150, sm: 180 },
                              textAlign: { xs: 'center', sm: 'center' },
                              fontWeight: 600,
                              borderRadius: '0px 8px 0px 0px',
                              backgroundColor: '#f5f5f5',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            Price per Return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            Single Vehicle
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#1da94a',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $19.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            2 Vehicles
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $24.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            3 to 5 Vehicles
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $29.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            6 to 10 Vehicles
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $39.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            11 to 25 Vehicles
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $49.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            26 to 100 Vehicles
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: 2,
                              borderColor: '#a6a6a6',
                            }}
                          >
                            $79.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            101 to 500 Vehicles
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: '2px solid #a6a6a6 !important',
                            }}
                          >
                            $99.99
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            scope="row"
                            sx={{
                              fontSize: 18,
                            }}
                          >
                            500+ Vehicles
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: '#000',
                              borderLeft: '2px solid #a6a6a6 !important',
                            }}
                          >
                            $149.99
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
              {/*  <Grid
              size={12}
              
              textAlign="center"
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mt: 1,
                }}
              >
                E-File Form 2290 with the IRS and get the stamped Schedule 1
                immediately
              </Typography>
              <Button
                
                variant="contained"
                sx={{
                  mt: { xs: 2, sm: 4 },
                  backgroundColor: '#1da94a !important',
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
              >
                E-File Form 2290
              </Button>
            </Grid>
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
            </Grid> */}
              <Grid
                size={{ xs: 12, sm: 6, md: 6, lg: 5, xl: 4.5 }}
                textAlign="center"
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 3 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Flexi-Fleet
                  </Typography>
                  <Typography variant="body1">
                    E-file unlimited <b>Form 2290</b> for your business in a tax
                    year
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#1da94a',
                      mb: 0,
                    }}
                  >
                    $179.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /business
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, md: 6, lg: 5, xl: 4.5 }}
                textAlign="center"
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 3 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Flexi-Fleet Plus
                  </Typography>
                  <Typography variant="body1">
                    E-file unlimited <b>Form 2290</b> and <b>8849</b> for your
                    business in a tax year
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $199.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /business
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              {/*  <Grid
              size={12}
              
              textAlign="center"
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mt: 1,
                }}
              >
                E-File Form 2290 with the IRS and get the stamped Schedule 1
                immediately
              </Typography>
              <Button
               
                variant="contained"
                sx={{
                  mt: { xs: 2, sm: 4 },
                  backgroundColor: '#1da94a !important',
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
              >
                E-File Form 2290
              </Button>
            </Grid>
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
            </Grid> */}
              <Grid size={12} textAlign="center">
                <TableContainer
                  sx={{
                    mt: 1,
                    border: '2px solid #a6a6a6',
                    borderRadius: 2,
                    maxWidth: 836,
                    mx: 'auto',
                  }}
                >
                  <Table
                    sx={{
                      '& td': {
                        display: { xs: 'block', sm: 'revert' },
                      },
                      '& tr td:first-of-type': {
                        pt: { xs: 1, sm: 0.5 },
                        pb: { xs: 0, sm: 0.5 },
                        borderBottom: {
                          xs: 0,
                          sm: '1px solid #a6a6a6',
                        },
                        textAlign: { xs: 'center', sm: 'left' },
                      },
                      '& tr td:last-child': {
                        pt: { xs: 0, sm: 0.5 },
                        pb: { xs: 0.5, sm: 0.5 },
                        borderBottom: {
                          xs: '1px solid #a6a6a6',
                          sm: '1px solid #a6a6a6',
                        },
                        textAlign: { xs: 'center', sm: 'right' },
                      },
                      '& tr:last-child td': { border: 0 },
                    }}
                    aria-label="price table"
                    className="table-list"
                  >
                    <TableBody sx={{}}>
                      <TableRow>
                        <TableCell scope="row" sx={{ fontSize: 18 }}>
                          Form 8849 e-Filing
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: 24, fontWeight: 700, color: '#000' }}
                        >
                          $29.99
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell scope="row" sx={{ fontSize: 18 }}>
                          Form 2290 Amendment e-Filing - VIN Correction
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: '#1da94a',
                          }}
                        >
                          FREE
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell scope="row" sx={{ fontSize: 18 }}>
                          Form 2290 Amendment e-Filing - Weight Increased
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: 24, fontWeight: 700, color: '#000' }}
                        >
                          $24.99
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{}}>
                        <TableCell scope="row" sx={{ fontSize: 18 }}>
                          Form 2290 Amendment e-Filing - Mileage Exceeded
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontSize: 24, fontWeight: 700, color: '#000' }}
                        >
                          $24.99
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid size={12} textAlign="center">
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    mt: 1,
                  }}
                >
                  E-file Form 2290 with the IRS and get the stamped Schedule 1
                  immediately
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 2, sm: 4 },
                    backgroundColor: '#1da94a !important',
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
                >
                  E-File Form 2290
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Grid>
        {/* Excise Tax Price end */}
        {/* Extension Price start */}
        <Grid
          component="section"
          container
          sx={{
            backgroundColor: '#f0f2f9',
            scrollMarginTop: 120,
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
            mb: 0,
          }}
          spacing={2}
          m={0}
          id="extension"
        >
          <Container maxWidth="lg">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={5}
              pb={0}
              className="MuiGrid-root-width"
            >
              <Grid size={12}>
                <Link
                  href="/tax-extension/"
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#213e99' },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 3,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    E-file IRS <b>Extension Forms</b> at the
                    <br className="hide-xs" /> lowest price in the industry
                  </Typography>
                </Link>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              className="MuiGrid-root-width"
              sx={{ justifyContent: 'center' }}
            >
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 7004
                  </Typography>
                  <Typography variant="body1">
                    Extension to File
                    <br />
                    Business Income Tax Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#213e99',
                      mb: 0,
                    }}
                  >
                    $17.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 4868
                  </Typography>
                  <Typography variant="body1">
                    Extension to File
                    <br />
                    Individual Income Tax Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $11.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#ffff',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 8868
                  </Typography>
                  <Typography variant="body1">
                    Extension to File
                    <br />
                    Exempt Organization Return
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#213e99',
                      mb: 0,
                    }}
                  >
                    $14.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                    // background:
                    //   'linear-gradient(99deg,rgba(107, 245, 255, 0.23) 0%, rgba(6, 107, 189, 0.19) 100%)',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 8809
                  </Typography>
                  <Typography variant="body1">
                    Extension to File
                    <br />
                    Information Returns
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#000',
                      mb: 0,
                    }}
                  >
                    $4.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                    backgroundColor: '#fff',
                    // background:
                    //   'linear-gradient(99deg,rgba(107, 245, 255, 0.23) 0%, rgba(6, 107, 189, 0.19) 100%)',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form 15397
                  </Typography>
                  <Typography variant="body1">
                    Request an Extension to
                    <br />
                    Furnish Recipient Statements
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#213e99',
                      mb: 0,
                    }}
                  >
                    $9.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /return
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                    fontWeight: 600,
                    mt: 3,
                    color: '#213e99',
                    textAlign: 'center',
                    textDecoration: 'none',
                  }}
                >
                  Tax Time Twins
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 2,
                    minHeight: '100%',
                    background: 'linear-gradient(135deg, #ddf1e8, #dadfed)',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-File 7004 and 4868
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#213e99',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $29.98
                    </Box>
                    <br />
                    $24.99
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $4.99
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#213e99',
                    borderRadius: 2,
                    minHeight: '100%',
                    background: 'linear-gradient(135deg, #ddf1e8, #dadfed)',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    E-File 8868 and 4868
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#213e99',
                      mb: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      $26.98
                    </Box>
                    <br />
                    $21.99
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#17813f',
                      fontWeight: 600,
                      background: '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      width: 'fit-content',
                      justifyContent: 'center',
                      px: 2,
                      py: 0.5,
                      borderRadius: 10,
                      mx: 'auto',
                      mt: 1,
                    }}
                  >
                    <DoneOutlinedIcon sx={{ mr: 0.5 }} />
                    Save $4.99
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={0}
            pb={2}
            mb={6}
            className="MuiGrid-root-width"
            sx={{ width: '100%' }}
          >
            <Grid size={12} textAlign="center" mt={3} mb={0}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                }}
              >
                File an extension form with the IRS and get an extra time of up
                to 6 months.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mt: { xs: 2, sm: 4 },
                  backgroundColor: '#213e99 !important',
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
              >
                Get Extension Now
              </Button>
            </Grid>
          </Grid>
        </Grid>
        {/* Extension Price end */}

        {/* Essential Price start */}
        <Grid
          component="section"
          container
          sx={{
            backgroundColor: '#fff',
            scrollMarginTop: 120,
            mx: '0 !important',
            width: '100% !important',
            backgroundPosition: 'center bottom',
            mb: 0,
          }}
          spacing={2}
          m={0}
          id="essential"
        >
          <Container maxWidth="lg">
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={5}
              pb={0}
              className="MuiGrid-root-width"
            >
              <Grid size={12}>
                <Box
                  component="div"
                  sx={{
                    textDecoration: 'none',
                    color: '#444',
                    '& b': { color: '#4b42ad' },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                      fontWeight: 600,
                      mb: 2,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    File Your <b>IRS and FinCEN Forms</b> - Fast, Secure
                    <br className="hide-xs" />
                    Filing at the Best Price
                  </Typography>

                  {/* <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 20, md: 20, sm: 18, xs: 18 },
                      fontWeight: 400,
                      mb: 3,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    BOI Reporting
                  </Typography> */}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form W-9
                  </Typography>
                  <Typography variant="body1">
                    Request for Taxpayer Identification Number and Certification
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#4b42ad',
                      mb: 0,
                    }}
                  >
                    FREE
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Form W-8BEN
                  </Typography>
                  <Typography variant="body1">
                    Certificate of Foreign Status of Beneficial Owner
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#333',
                      mb: 0,
                    }}
                  >
                    FREE
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5.5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#a6a6a6',
                    borderRadius: 2,
                    minHeight: '100%',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: { xs: 0, sm: 2, md: 1 },
                    }}
                  >
                    Form 8655
                  </Typography>
                  <Typography variant="body1">
                    Reporting Agent Authorization
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 4,
                      color: '#4b42ad',
                      mb: 0,
                    }}
                  >
                    $9.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /Form
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} textAlign="center">
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    py: { xs: 2, sm: 3, md: 5 },
                    textAlign: 'center',
                    borderWidth: 2,
                    borderColor: '#305653',
                    borderRadius: 2,
                    minHeight: '100%',
                    background: '#cbedeb',
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 22 },
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    BOI Report
                  </Typography>
                  <Typography variant="body1">
                    Beneficial Ownership Information Report
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: 28, sm: 34, md: 40 },
                      fontWeight: 700,
                      mt: 2,
                      color: '#2c5257',
                      mb: 0,
                    }}
                  >
                    $39.99
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: 18, sm: 24, md: 30 },
                        fontWeight: 300,
                        color: '#444',
                      }}
                    >
                      /EIN
                    </Box>
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              m={2}
              mt={0}
              pb={2}
              mb={6}
              className="MuiGrid-root-width"
            >
              <Grid size={12} textAlign="center">
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                    fontWeight: 400,
                    mt: 1,
                  }}
                >
                  Submit everything you need to stay compliant—all in one secure
                  platform.
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 2, sm: 2 },
                    backgroundColor: '#4b42ad !important',
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
                >
                  Get Started Now!
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Grid>
        {/* Essential Price end */}
      </Box>
      {/* Common page text */}
      <Box
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
        component="section"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
          }}
        >
          Take the first step toward your IRS form filing with TaxZerone
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
          }}
        >
          Choose TaxZerone for simple, quick, and secure e-filing.
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=pricing`}
          >
            E-file Now
          </Button>
        </Box>
      </Box>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',

          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
        component="section"
      >
        <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Complete the IRS form filing in just 3 simple steps
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Filing an IRS form using TaxZerone is so simple that you can
                complete
                <Hidden smDown>
                  <br />
                </Hidden>{' '}
                the whole process in just 5 minutes.
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            m={2}
            pb={5}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: { xs: 2, sm: 3, md: 5 },
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/business-icon.webp" />
                    <source srcSet="/assets/images/business-icon.png" />
                    <img
                      src="/assets/images/business-icon.png"
                      alt="Enter form information"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Box
                  component="span"
                  sx={{
                    background: '#213e99',
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'inline-block',
                    mb: 1,
                    minWidth: 75,
                  }}
                >
                  Step 1
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                  }}
                >
                  Enter form information
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: { xs: 2, sm: 3, md: 5 },
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/review-icon.webp" />
                    <source srcSet="/assets/images/review-icon.png" />
                    <img
                      src="/assets/images/review-icon.png"
                      alt="Review the form"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Box
                  component="span"
                  sx={{
                    background: '#1f7272',
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'inline-block',
                    mb: 1,
                    minWidth: 75,
                  }}
                >
                  Step 2
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                  }}
                >
                  Review the form
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: { xs: 2, sm: 3, md: 5 },
                  textAlign: 'center',
                  borderWidth: 2,
                  borderColor: 'transparent',
                  borderRadius: 5,
                  height: '100%',
                  boxShadow: '10px 0px 30px rgba(91,98,117,.15)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#efefef',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source srcSet="/assets/images/transmit-icon.webp" />
                    <source srcSet="/assets/images/transmit-icon.png" />
                    <img
                      src="/assets/images/transmit-icon.png"
                      alt="Transmit the return to the IRS"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Box
                  component="span"
                  sx={{
                    background: '#1da94a',
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'inline-block',
                    mb: 1,
                    minWidth: 75,
                  }}
                >
                  Step 3
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                  }}
                >
                  Transmit the return to the IRS
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container
        maxWidth="lg"
        component="section"
        aria-labelledby="pricing-faqs"
      >
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              id="pricing-faqs"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              FAQs on pricing
            </Typography>
            <Box component="div" sx={{ mb: 5 }}>
              <Box
                component="article"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                >
                  1. What are the accepted payment methods to file an IRS form?
                </Typography>

                <Typography variant="body1">
                  We support payments using credit and debit cards, including
                  all popular card types such as Mastercard, American Express,
                  and Visa.
                </Typography>
              </Box>
              <Box
                component="article"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                >
                  2. Can I pay my tax due while filing an IRS form?
                </Typography>

                <Typography variant="body1">
                  Yes, you can choose to pay the tax due by choosing a payment
                  method such as EFW,{' '}
                  <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                    EFTPS
                  </Link>
                  , credit/debit card, and check or money order.
                </Typography>
              </Box>
              <Box
                component="article"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  // itemProp="name"
                >
                  3. What happens if my IRS form is rejected?
                </Typography>

                <Typography variant="body1">
                  You can correct the errors and retransmit it to the IRS with
                  TaxZerone for free. You won&apos;t be charged any additional
                  amount.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  borderWidth: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover, &:focus, &:active': { borderWidth: 2 },
                }}
                href="/faqs/"
              >
                More FAQs
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Pricing;
